import React, { useState, useEffect, useCallback, useRef } from 'react'
import { WIDGET_TYPES, DEFAULT_CONFIG, DEFAULT_WIDGET_SIZE_BY_TYPE, WIDGET_SIZE_PRESETS, ENTITY_ICONS } from './constants'
import { fetchEntities, fetchFilterMeta, fetchWidgetData } from './api'
import { initSelect2InModal, destroySelect2InModal, syncSelect2InModal } from '../lib/select2Modal'

function formatShortDate(isoDate) {
  if (!isoDate) return ''
  const parts = String(isoDate).split('T')[0].split('-')
  if (parts.length < 3) return isoDate
  return `${parts[2]}.${parts[1]}.${parts[0]}`
}

function formatBarLabel(isoDate) {
  if (!isoDate) return ''
  const parts = String(isoDate).split('-')
  const day = parts[2] ? parseInt(parts[2], 10) : 0
  const month = parts[1] ? parseInt(parts[1], 10) : 0
  const d = day < 10 ? '0' + day : String(day)
  const m = month < 10 ? '0' + month : String(month)
  return `${d}.${m}`
}

const PREVIEW_STATE_LABELS = { draft: 'Создание', validation: 'Проверка', review: 'Согласование', done: 'Выполнено' }
const PREVIEW_DONUT_COLORS = { draft: '#28c76f', validation: '#ff9f43', review: '#00cfe8', done: '#6c757d' }

function WidgetPreview({ type, title, data }) {
  const displayTitle = title || (type === 'counter' || type === 'list' ? 'Заявки' : type === 'bar' ? 'Гистограмма' : 'Распределение')
  if (!data || data.error) return null
  if (type === 'counter') {
    const value = typeof data.value === 'number' ? data.value : '—'
    return (
      <div className="sigur-widgets__stats">
        <div className="sigur-stat">
          <div className="sigur-stat__head">
            <div className="sigur-stat__icon"><i className="ti ti-chart-bar" /></div>
            <span className="sigur-stat__value">{value}</span>
          </div>
          <span className="sigur-stat__label">{displayTitle}</span>
        </div>
      </div>
    )
  }
  if (type === 'list') {
    const items = Array.isArray(data.items) ? data.items.slice(0, 3) : []
    const isRequestFormat = items.length > 0 && items[0].state !== undefined
    return (
      <div className="sigur-my-requests sigur-my-requests--no-pointer">
        <h3 className="sigur-chart__title mb-3">{displayTitle}</h3>
        {items.length > 0 ? (
          <div className="sigur-my-requests__list">
            {items.map((item) => {
              if (isRequestFormat) {
                const metaParts = []
                if (item.d_last_transition) metaParts.push(`Переход: ${formatShortDate(item.d_last_transition)}`)
                if (item.visit_purpose) metaParts.push(`Цель: ${item.visit_purpose}`)
                if (item.territory) metaParts.push(`Территория: ${item.territory}`)
                return (
                  <div key={item.id} className="sigur-my-requests__card">
                    <i className="ti ti-file-text sigur-my-requests__icon" aria-hidden />
                    <div className="sigur-my-requests__body">
                      <div className="sigur-my-requests__row">
                        <span className="sigur-my-requests__title">Заявка #{item.id}</span>
                        <span className="sigur-my-requests__badge" style={{ backgroundColor: PREVIEW_DONUT_COLORS[item.state] || '#6c757d' }}>
                          {PREVIEW_STATE_LABELS[item.state] || item.state}
                        </span>
                      </div>
                      {metaParts.length > 0 && <span className="sigur-my-requests__meta">{metaParts.join(' · ')}</span>}
                    </div>
                  </div>
                )
              }
              const itemTitle = item.title != null ? item.title : `#${item.id}`
              return (
                <div key={item.id} className="sigur-my-requests__card">
                  <i className="ti ti-file-text sigur-my-requests__icon" aria-hidden />
                  <div className="sigur-my-requests__body">
                    <div className="sigur-my-requests__row">
                      <span className="sigur-my-requests__title">{itemTitle}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-muted small mb-0">Нет записей</p>
        )}
      </div>
    )
  }
  if (type === 'bar') {
    const labels = Array.isArray(data.labels) ? data.labels : []
    const values = Array.isArray(data.values) ? data.values : []
    const maxVal = values.length ? Math.max(...values, 1) : 1
    const isMonth = values.length > 14
    return (
      <div className="sigur-chart sigur-chart--bar">
        <h3 className="sigur-chart__title mb-2">{displayTitle}</h3>
        <div className="sigur-chart__bar-wrap">
          {values.map((val, i) => {
            const barPercent = (val / maxVal) * 100
            const showLabel = !isMonth || i % 5 === 0 || i === values.length - 1
            const label = showLabel ? (labels[i] ? formatBarLabel(labels[i]) : labels[i] || String(i + 1)) : ''
            return (
              <div key={i} className="sigur-chart__bar-item">
                <div className="sigur-chart__bar-track">
                  <div className="sigur-chart__bar-fill" style={{ height: Math.max(2, barPercent) + '%' }} />
                </div>
                <span className="sigur-chart__bar-label">{label}</span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  if (type === 'donut') {
    const items = Array.isArray(data.items) ? data.items : []
    const total = data.total ?? 0
    return (
      <div className="sigur-chart sigur-chart--donut">
        <h3 className="sigur-chart__title">{displayTitle}</h3>
        <div className="sigur-chart__donut-wrap">
          <div
            className="sigur-chart__donut"
            style={{
              background: items.length
                ? `conic-gradient(${items.map((s, i) => {
                    const prev = items.slice(0, i).reduce((a, x) => a + (x.percent || 0), 0)
                    return `${s.color || '#6c757d'} ${prev}% ${prev + (s.percent || 0)}%`
                  }).join(', ')})`
                : 'conic-gradient(#e9ecef 0% 100%)',
            }}
          >
            <div className="sigur-chart__donut-hole">
              <span className="sigur-chart__donut-total">{total}</span>
              <span className="sigur-chart__donut-sublabel">всего</span>
            </div>
          </div>
        </div>
        <div className="sigur-chart__legend">
          {items.length ? items.map((s) => (
            <div key={s.label} className="sigur-chart__legend-item">
              <span className="sigur-chart__legend-dot" style={{ backgroundColor: s.color || '#6c757d' }} />
              <span>{s.label} — {s.percent}%</span>
            </div>
          )) : <span className="sigur-chart__legend-empty">Нет данных</span>}
        </div>
      </div>
    )
  }
  return null
}

const STEPS = [
  { key: 'type', title: 'Тип виджета', shortTitle: 'Тип' },
  { key: 'entity', title: 'Раздел (ДО)', shortTitle: 'Раздел' },
  { key: 'filters', title: 'Фильтры', shortTitle: 'Фильтры' },
  { key: 'options', title: 'Опции и подпись', shortTitle: 'Опции' },
  { key: 'preview', title: 'Превью', shortTitle: 'Превью' },
]

function buildConfig(form) {
  const config = {
    type: form.type || 'counter',
    entity: form.entity || 'requests',
    filters: form.filters && Object.keys(form.filters).length ? form.filters : undefined,
    title: form.title || undefined,
  }
  if (form.type === 'list') {
    config.limit = Math.min(50, Math.max(1, parseInt(form.limit, 10) || 10))
  }
  if (form.type === 'bar' || form.type === 'donut') {
    config.groupBy = form.groupBy || (form.type === 'donut' ? 'state' : 'date')
  }
  if (form.type === 'bar') {
    config.period = form.period || 'week'
  }
  return config
}

function updateFormBySelect2(prev, name, val) {
  if (name.startsWith('filter_')) {
    const key = name.slice(7)
    return { ...prev, filters: { ...(prev.filters || {}), [key]: val || undefined } }
  }
  return { ...prev, [name]: val }
}

export default function WidgetConstructorWizard({ modalRef, onSave, onClose, initialConfig, widgetId }) {
  const isEdit = Boolean(initialConfig && widgetId)
  const [step, setStep] = useState(1)
  const [form, setForm] = useState(() => {
    if (initialConfig) {
      return {
        type: initialConfig.type || 'counter',
        entity: initialConfig.entity || 'requests',
        filters: initialConfig.filters || {},
        title: initialConfig.title || '',
        limit: initialConfig.limit ?? 10,
        groupBy: initialConfig.groupBy || 'date',
        period: initialConfig.period || 'week',
        sizeOverride: initialConfig.sizeOverride || null,
      }
    }
    return { ...DEFAULT_CONFIG, sizeOverride: null }
  })
  const [entities, setEntities] = useState([])
  const [filterFields, setFilterFields] = useState([])
  const [previewData, setPreviewData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [entitiesLoading, setEntitiesLoading] = useState(true)
  const [entitiesError, setEntitiesError] = useState(null)
  const [filterMetaError, setFilterMetaError] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [limitError, setLimitError] = useState(null)
  const previewRequestIdRef = useRef(0)

  const entitiesForType = entities.filter((e) => e.types && e.types.includes(form.type))

  useEffect(() => {
    fetchEntities((err, list) => {
      setEntitiesLoading(false)
      setEntitiesError(err || null)
      if (!err && list) setEntities(list)
    })
  }, [])

  useEffect(() => {
    if (!form.entity) {
      setFilterFields([])
      setFilterMetaError(null)
      return
    }
    setFilterMetaError(null)
    fetchFilterMeta(form.entity, (err, fields) => {
      setFilterMetaError(err || null)
      setFilterFields(Array.isArray(fields) ? fields : [])
    })
  }, [form.entity])

  const loadPreview = useCallback(() => {
    const config = buildConfig(form)
    const id = previewRequestIdRef.current + 1
    previewRequestIdRef.current = id
    setPreviewLoading(true)
    setPreviewData(null)
    fetchWidgetData(config, (err, data) => {
      if (previewRequestIdRef.current !== id) return
      setPreviewLoading(false)
      if (!err && data && !data.error) setPreviewData(data)
    })
  }, [form])

  useEffect(() => {
    if (step === 5) loadPreview()
    return () => { previewRequestIdRef.current += 1 }
  }, [step, loadPreview])

  const handleSelect2Change = useCallback((name, val) => {
    setForm((prev) => updateFormBySelect2(prev, name, val))
  }, [])

  useEffect(() => {
    const el = modalRef?.current
    if (!el) return
    destroySelect2InModal(el)
    initSelect2InModal(el, handleSelect2Change)
    return () => destroySelect2InModal(el)
  }, [modalRef, step, handleSelect2Change])

  useEffect(() => {
    const el = modalRef?.current
    if (el) setTimeout(() => syncSelect2InModal(el), 0)
  }, [modalRef, step])

  const handleSave = () => {
    setSaveError(null)
    const config = buildConfig(form)
    const size = form.sizeOverride ? { w: form.sizeOverride.w, h: form.sizeOverride.h } : undefined
    const done = (err) => {
      if (err) setSaveError(err.message || String(err))
      else onClose()
    }
    onSave(config, widgetId, { size }, done)
  }

  const validateLimit = (val) => {
    const n = parseInt(val, 10)
    if (Number.isNaN(n) || n < 1 || n > 50) return true
    return false
  }

  const canNext = () => {
    if (step === 1) return true
    if (step === 2) {
      if (!form.entity) return false
      if (entitiesForType.some((e) => e.id === form.entity)) return true
      if (entitiesLoading) return true
      return false
    }
    return true
  }

  const canSave = () => {
    if (form.type === 'list' && limitError) return false
    return true
  }

  const totalSteps = STEPS.length
  const isLastStep = step === totalSteps

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title">
          {isEdit ? 'Редактировать виджет' : 'Создать виджет'}
        </h5>
        <button type="button" className="btn-close" onClick={onClose} aria-label="Закрыть" />
      </div>
      <div className="modal-body">
        <nav className="sigur-widget-wizard__stepper" aria-label="Шаги">
          {STEPS.map((s, i) => {
            const stepNum = i + 1
            const isActive = step === stepNum
            const isPast = step > stepNum
            const isClickable = isPast || isActive
            const goToStep = () => {
              if (isClickable && (isPast || isActive)) setStep(stepNum)
            }
            return (
              <React.Fragment key={s.key}>
                <button
                  type="button"
                  className={`sigur-widget-wizard__stepper-step ${isActive ? 'sigur-widget-wizard__stepper-step--active' : ''} ${isPast ? 'sigur-widget-wizard__stepper-step--done' : ''}`}
                  onClick={goToStep}
                  disabled={!isClickable}
                  aria-current={isActive ? 'step' : undefined}
                  aria-disabled={!isClickable}
                >
                  <span className="sigur-widget-wizard__stepper-num">
                    {isPast ? <i className="ti ti-check" aria-hidden /> : stepNum}
                  </span>
                  <span className="sigur-widget-wizard__stepper-title">{s.shortTitle}</span>
                </button>
                {i < STEPS.length - 1 && <span className="sigur-widget-wizard__stepper-line" aria-hidden />}
              </React.Fragment>
            )
          })}
        </nav>

        <div className="sigur-widget-wizard__step-body">
          {/* Step 1: Type — карточки */}
          {step === 1 && (
            <div className="mb-0">
              <h6 className="sigur-widget-wizard__step-heading">{STEPS[0].title}</h6>
              <div className="sigur-widget-wizard__type-cards">
                {WIDGET_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`sigur-widget-wizard__type-card ${form.type === t.id ? 'sigur-widget-wizard__type-card--selected' : ''}`}
                    onClick={() => setForm((f) => ({ ...f, type: t.id }))}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setForm((f) => ({ ...f, type: t.id })) } }}
                    title={t.label}
                  >
                    <i className={t.icon || 'ti ti-chart-bar'} aria-hidden />
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Entity — карточки */}
          {step === 2 && (
            <div className="mb-0">
              <h6 className="sigur-widget-wizard__step-heading">{STEPS[1].title}</h6>
              {entitiesError && (
                <p className="text-danger small mb-2">{entitiesError.message || String(entitiesError)}</p>
              )}
              {entitiesLoading ? (
                <p className="text-muted small mb-0">Загрузка разделов…</p>
              ) : (
                <div className="sigur-widget-wizard__entity-cards">
                  {entitiesForType.map((ent) => (
                    <button
                      key={ent.id}
                      type="button"
                      className={`sigur-widget-wizard__entity-card ${form.entity === ent.id ? 'sigur-widget-wizard__entity-card--selected' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, entity: ent.id, filters: {} }))}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setForm((f) => ({ ...f, entity: ent.id, filters: {} })) } }}
                      title={ent.title}
                    >
                      <i className={ENTITY_ICONS[ent.id] || 'ti ti-folder'} aria-hidden />
                      <span>{ent.title}</span>
                    </button>
                  ))}
                  {entitiesForType.length === 0 && !entitiesLoading && (
                    <button
                      type="button"
                      className="sigur-widget-wizard__entity-card sigur-widget-wizard__entity-card--selected"
                      onClick={() => setForm((f) => ({ ...f, entity: 'requests', filters: {} }))}
                      title="Заявки"
                    >
                      <i className={ENTITY_ICONS.requests || 'ti ti-folder'} aria-hidden />
                      <span>Заявки</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Filters */}
          {step === 3 && (
            <div className="mb-3">
              <h6 className="sigur-widget-wizard__step-heading">{STEPS[2].title}</h6>
              {filterMetaError && (
                <p className="text-danger small mb-2">{filterMetaError.message || String(filterMetaError)}</p>
              )}
            {filterFields.length === 0 && !filterMetaError ? (
              <p className="text-muted small">Нет доступных фильтров для выбранного раздела.</p>
            ) : (
              filterFields.map((field) => (
                <div key={field.name} className="mb-2">
                  <label className="form-label small">{field.label}</label>
                  <select
                    name={`filter_${field.name}`}
                    className="form-select form-select-sm js-select2-modal"
                    value={form.filters?.[field.name] ?? ''}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      filters: { ...(f.filters || {}), [field.name]: e.target.value || undefined },
                    }))}
                  >
                    <option value="">Не задано</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              ))
            )}
            </div>
          )}

          {/* Step 4: Options */}
          {step === 4 && (
          <>
            {(form.type === 'bar' || form.type === 'donut') && (
              <div className="mb-3">
                <label className="form-label" title="Группировка данных для графика">Группировка</label>
                <select
                  name="groupBy"
                  className="form-select js-select2-modal"
                  value={form.groupBy || (form.type === 'donut' ? 'state' : 'date')}
                  onChange={(e) => setForm((f) => ({ ...f, groupBy: e.target.value }))}
                  title="Группировка данных для графика"
                >
                  {form.type === 'bar' && <option value="date">По дате</option>}
                  <option value="state">По статусу</option>
                </select>
              </div>
            )}
            {form.type === 'bar' && (form.groupBy || 'date') === 'date' && (
              <div className="mb-3">
                <label className="form-label" title="Временной интервал для гистограммы">Период</label>
                <select
                  name="period"
                  className="form-select js-select2-modal"
                  value={form.period || 'week'}
                  onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}
                  title="Временной интервал для гистограммы"
                >
                  <option value="week">Неделя</option>
                  <option value="month">Месяц</option>
                </select>
              </div>
            )}
            {form.type === 'list' && (
              <div className="mb-3">
                <label className="form-label" title="Максимальное количество записей в списке (1–50)">Лимит записей</label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  max={50}
                  value={form.limit ?? 10}
                  onChange={(e) => {
                    const v = e.target.value
                    setForm((f) => ({ ...f, limit: v }))
                    setLimitError(validateLimit(v))
                  }}
                  onBlur={(e) => setLimitError(validateLimit(e.target.value))}
                  title="Максимальное количество записей в списке (1–50)"
                />
                {limitError && (
                  <p className="text-danger small mt-1 mb-0">Введите число от 1 до 50</p>
                )}
              </div>
            )}
            {!isEdit && (
              <div className="mb-3">
                <label className="form-label" title="Ширина и высота виджета на сводке">Размер виджета</label>
                <select
                  className="form-select js-select2-modal"
                  value={WIDGET_SIZE_PRESETS.find((p) => (p.size && form.sizeOverride && p.size.w === form.sizeOverride.w && p.size.h === form.sizeOverride.h) || (!p.size && !form.sizeOverride))?.id ?? 'default'}
                  onChange={(e) => {
                    const preset = WIDGET_SIZE_PRESETS.find((p) => p.id === e.target.value)
                    setForm((f) => ({ ...f, sizeOverride: preset && preset.size ? preset.size : null }))
                  }}
                >
                  {WIDGET_SIZE_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label" title="Заголовок виджета на сводке">Подпись виджета</label>
              <input
                type="text"
                className="form-control"
                placeholder="Например: Заявки в работе"
                title="Заголовок виджета на сводке"
                value={form.title ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
          </>
          )}

          {/* Step 5: Preview */}
          {step === 5 && (
            <div className="mb-3">
              <h6 className="sigur-widget-wizard__step-heading">{STEPS[4].title}</h6>
            {previewLoading && !previewData && <p className="text-muted small">Загрузка…</p>}
            {!previewLoading && previewData && previewData.error && (
              <p className="text-danger small">{previewData.error}</p>
            )}
            {!previewLoading && previewData && !previewData.error && (
              <div className="sigur-widget-preview border p-3 bg-white rounded">
                <WidgetPreview type={form.type} title={form.title} data={previewData} />
              </div>
            )}
            </div>
          )}
        </div>
      </div>
      <div className="modal-footer">
        {step > 1 && (
          <button
            type="button"
            className="btn btn-outline-secondary me-auto"
            onClick={(e) => {
              setStep((s) => s - 1)
              e.currentTarget.blur()
            }}
          >
            Назад
          </button>
        )}
        <div className="d-flex gap-2 ms-auto">
          {!isLastStep ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => {
                setStep((s) => s + 1)
                e.currentTarget.blur()
              }}
              disabled={!canNext()}
            >
              Далее
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => {
                handleSave()
                e.currentTarget.blur()
              }}
              disabled={!canSave()}
            >
              {isEdit ? 'Сохранить' : 'Добавить на сводку'}
            </button>
          )}
        </div>
      </div>
      {saveError && (
        <div className="modal-body border-top pt-2">
          <p className="text-danger small mb-0">{saveError}</p>
        </div>
      )}
    </>
  )
}

export { buildConfig, DEFAULT_WIDGET_SIZE_BY_TYPE }
